import React, { Fragment, useState } from 'react';
import moment from 'moment';
import styles from './styles.scss';

import DualCaptureViewer from '../../CaptureViewer/DualCaptureViewer';
import { CaptureGroup, MultiPointInstallation } from '../../../types/api';
import { ConfigStatus, ConfigType } from '../../../types/sensor-configuration';
import {
  HighlightableCaptureGroup,
} from '../../../types/multi-point-installation-captures';
import {
  AppBar,
  AppBarContext,
  AppBarSection,
  Icons,
  RadioButton,
} from '@densityco/ui';

interface Props {
  captureGroup: CaptureGroup;
  mpi: MultiPointInstallation;
  seek: number;
  selectedCaptureGroupId: CaptureGroup['id'] | null; // TODO: is this necessary?
}

function CaptureViewer({ captureGroup, mpi, seek, selectedCaptureGroupId }: Props) {

  const [selectedSensorIndex, setSelectedSensorIndex] = useState(0);
  const reversedSensors = mpi.sensors.slice().reverse();
  const captureViewerLeftSensor = reversedSensors[selectedSensorIndex];
  const captureViewerRightSensor = reversedSensors[selectedSensorIndex + 1];

  const captureViewerLeftCapture = captureGroup
    ? captureGroup.captures.find(
      c => c.serial_number === captureViewerLeftSensor.sensor_serial,
    )
    : null;
  const captureViewerRightCapture = captureGroup
    ? captureGroup.captures.find(
      c => c.serial_number === captureViewerRightSensor.sensor_serial,
    )
    : null;

  //
  // Using the "video_starts_at" field from each capture, calculate the offset between all
  // captures playing back, so we can show them all synced up
  //

  const allCapturesHaveVideoStartsAt = captureGroup ? captureGroup.captures.every(
    c => c.video_starts_at !== null,
  ) : false;

  // Return the epoch representation for the time that the capture was initiated.
  const getCaptureStartEpoch = (
    capture: HighlightableCaptureGroup['captures'][0],
  ): number => {
    if (capture.video_starts_at === null) {
      // This error should never be thrown because of the `allCapturesHaveVideoStartsAt` check
      // above, and is just here to make typescript happy
      throw new Error(
        `Capture ${capture.id} does not have a "video_starts_at" value; this is required to view this mpi.`,
      );
    }
    return moment.utc(capture.video_starts_at).valueOf() / 1000;
  };

  const largestEpoch = allCapturesHaveVideoStartsAt
    ? Math.max(
      ...(captureGroup
        ? captureGroup.captures.map(getCaptureStartEpoch)
        : null),
    )
    : null;

  const captureViewerLeftOffset =
    allCapturesHaveVideoStartsAt && captureViewerLeftCapture
      ? largestEpoch - getCaptureStartEpoch(captureViewerLeftCapture)
      : null;

  const captureViewerRightOffset =
    allCapturesHaveVideoStartsAt && captureViewerRightCapture
      ? largestEpoch - getCaptureStartEpoch(captureViewerRightCapture)
      : null;


  const captureViewerRadioButtons = [];
  for (let i = 0; i <= mpi.sensors.length - 2; i += 1) {
    captureViewerRadioButtons.push(
      <div className={styles.captureViewerRadioButtonWrapper} key={i}>
        <RadioButton
          checked={selectedSensorIndex === i}
          onChange={() => setSelectedSensorIndex(i)}
        />
      </div>,
    );
  }

  const showCaptureViewer =
    // Left capture is populated and has proper offset
    (captureViewerLeftCapture && captureViewerLeftOffset !== null) ||
    // Right capture is populated and has proper offset
    (captureViewerRightCapture && captureViewerRightOffset !== null) ||
    // Neither capture exists on this page, BUT there is at least one capture (on another page)
    (!captureViewerLeftCapture &&
      !captureViewerRightCapture &&
      (captureGroup && captureGroup.captures.length > 0));


  if (showCaptureViewer) {
    return (
      <Fragment>
        <AppBarContext.Provider value="CARD_HEADER">
          <AppBar>
            <AppBarSection>
              <strong>Group ID:</strong>&nbsp;{selectedCaptureGroupId}
            </AppBarSection>
            <AppBarSection>
              <button
                onClick={() =>
                  setSelectedSensorIndex(selectedSensorIndex - 1)
                }
                disabled={selectedSensorIndex <= 0}
                className={styles.captureViewerChevronButton}
              >
                <Icons.ChevronLeft color="currentColor" />
              </button>
              {captureViewerRadioButtons}
              <button
                onClick={() =>
                  setSelectedSensorIndex(selectedSensorIndex + 1)
                }
                className={styles.captureViewerChevronButton}
                disabled={
                  selectedSensorIndex + 1 >= mpi.sensors.length - 1
                }
              >
                <Icons.ChevronRight color="currentColor" />
              </button>
            </AppBarSection>
          </AppBar>
        </AppBarContext.Provider>
        <DualCaptureViewer
          key={`dual-capture-viewer-${seek}-${captureViewerLeftCapture}-${captureViewerRightCapture}`}
          initialStartTimeSeconds={seek}
          leftSensor={{
            serialNumber: captureViewerLeftSensor.sensor_serial,
            index: captureViewerLeftSensor.position,
            organizationId: captureViewerLeftSensor.organization.id,
            doorwayName:
              captureViewerLeftSensor.doorway_name || 'N/A',

            configState: {
              config: captureViewerLeftSensor.assigned_config,
              configTemplate:
                captureViewerLeftSensor.assigned_config_template,
              status: captureViewerLeftSensor.config_status,
            },
          }}
          leftCapture={
            captureViewerLeftCapture
              ? {
                id: captureViewerLeftCapture.id,
                status: captureViewerLeftCapture.status,
                tags: [],
                capture_url: captureViewerLeftCapture.video_url,
                inferences: captureViewerLeftCapture.inferences,
                has_invalid_annotations:
                  captureViewerLeftCapture.has_invalid_annotations,
                has_invalid_algo_events:
                  captureViewerLeftCapture.has_invalid_annotations,
                latest_report: {
                  annotated_events:
                    captureViewerLeftCapture.annotations,
                  algo_events:
                    captureViewerLeftCapture.algo_events,
                },
              }
              : null
          }
          leftOffsetInSeconds={captureViewerLeftOffset}
          rightSensor={{
            serialNumber: captureViewerRightSensor.sensor_serial,
            index: captureViewerRightSensor.position,
            organizationId:
              captureViewerRightSensor.organization.id,
            doorwayName:
              captureViewerRightSensor.doorway_name || 'N/A',
            configState: {
              config: captureViewerRightSensor.assigned_config,
              configTemplate:
                captureViewerRightSensor.assigned_config_template,
              status: captureViewerRightSensor.config_status,
            },
          }}
          rightCapture={
            captureViewerRightCapture
              ? {
                id: captureViewerRightCapture.id,
                status: captureViewerRightCapture.status,
                tags: [],
                capture_url: captureViewerRightCapture.video_url,
                inferences: captureViewerRightCapture.inferences,
                has_invalid_annotations:
                  captureViewerRightCapture.has_invalid_annotations,
                has_invalid_algo_events:
                  captureViewerRightCapture.has_invalid_annotations,
                latest_report: {
                  annotated_events:
                    captureViewerRightCapture.annotations,
                  algo_events:
                    captureViewerRightCapture.algo_events,
                },
              }
              : null
          }
          rightOffsetInSeconds={captureViewerRightOffset}
          distanceInInches={
            captureViewerRightSensor.spacing_right /* because the order is reversed! */
          }
          overlappingRegionWidth={
            captureViewerRightSensor.overlap_right
          }
          maxXOffsetMultiplier={
            mpi.config.max_x_offset_multiplier
          }
          configState={{
            mutable_config: {
              filter: {
                max_pixel_intensity_override: 255,
              },
              trigger_line: {
                y0_position_override: 75,
                y1_position_override: 75,
                lower_bound_override: 10,
                upper_bound_override: 10,
              },
            },
            mutable_config_template: ConfigType.TRIGGER_LINE_BOTTOM,
            config_status: ConfigStatus.EXISTING,
          }}
          keyboardShortcutsEnabled
        />
      </Fragment>
    )
  } else if (!allCapturesHaveVideoStartsAt) {
    return (
      <div className={styles.viewerError}>
        <Fragment>
          <h3>Malformed capture(s) in capture group!</h3>
          <p>
            This MPI capture group contains at least one capture
            with an unset "video_starts_at" value.
          </p>
          <p>
            This value is <strong>required</strong> to calculate
            the offsets between MPI captures.
          </p>
        </Fragment>
      </div>
    )
  } else {
    return (
      <div className={styles.viewerError}>
        <Fragment>
          <h3>No captures found!</h3>
          <p>
            The selected capture group doesn't contain any captures.
          </p>
          <p>
            This group capture may have been made when the MPI was
            configured differently.
          </p>
        </Fragment>
      </div>
    )
  }
}

export default CaptureViewer;